/**
 * PokemonDetailModal Component
 *
 * Reusable modal for displaying Pokemon details across different screens.
 * Supports both base Pokemon data (from My Pokemon) and trained Pokemon data (from Hall of Fame).
 */

import React from 'react';
import { motion } from 'framer-motion';
import { X, Shield, Star, Swords, Zap, Clock } from 'lucide-react';
import { generatePokemonSprite, getAptitudeColor, getRarityColor } from '../utils/gameUtils';
import { TypeBadge, TypeIcon, TYPE_COLORS } from './TypeIcon';
import LimitBreakDiamonds from './LimitBreakDiamonds';
import StatRadarChart from './StatRadarChart';
import { POKEMON, LEGENDARY_POKEMON, MOVES } from '../shared/gameData';

// Color to type mapping for aptitudes
const colorToType = {
  Red: 'Fire',
  Blue: 'Water',
  Green: 'Grass',
  Yellow: 'Electric',
  Purple: 'Psychic',
  Orange: 'Fighting'
};

// Helper functions
const getGradeColor = (grade) => getAptitudeColor(grade);

const getStatTotal = (stats) => {
  return Object.values(stats || {}).reduce((sum, val) => sum + val, 0);
};

const PokemonDetailModal = ({
  pokemon,
  onClose,
  // Optional: For base Pokemon from inventory
  pokemonName,
  getPokemonRarity,
  getPokemonLimitBreak,
  applyLimitBreakToStats,
  // Optional: Additional metadata for trained Pokemon
  showTrainedMetadata = false,
  // Optional: Custom action buttons (function that receives pokemon data and returns JSX)
  renderActions = null
}) => {
  if (!pokemon && !pokemonName) return null;

  // Determine if this is a base Pokemon (from My Pokemon) or trained Pokemon (from Hall of Fame)
  const isBasePokemon = !!pokemonName;

  // Get Pokemon data
  const pokemonData = isBasePokemon
    ? (POKEMON[pokemonName] || LEGENDARY_POKEMON[pokemonName])
    : pokemon;

  if (!pokemonData) return null;

  // Calculate stats and metadata
  const limitBreakLevel = isBasePokemon ? getPokemonLimitBreak(pokemonName) : 0;
  const effectiveStats = isBasePokemon
    ? applyLimitBreakToStats(pokemonData.baseStats, limitBreakLevel)
    : pokemonData.stats;
  const statTotal = getStatTotal(effectiveStats);
  const rarity = isBasePokemon ? getPokemonRarity(pokemonName) : null;
  const grade = !isBasePokemon ? pokemonData.grade : null;
  const displayName = isBasePokemon ? pokemonName : pokemonData.name;
  const primaryType = pokemonData.primaryType || pokemonData.type;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-card-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 text-pocket-text-light hover:text-pocket-text rounded-lg"
        >
          <X size={20} />
        </button>

        {/* Pokemon sprite and name */}
        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            {generatePokemonSprite(primaryType, displayName, 64, false)}
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <h3 className="font-bold text-xl text-pocket-text">{displayName}</h3>
            {isBasePokemon && limitBreakLevel > 0 && (
              <LimitBreakDiamonds level={limitBreakLevel} size={12} />
            )}
          </div>
          <div className="flex justify-center gap-2 mt-2">
            <TypeBadge type={primaryType} size={16} />
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            {isBasePokemon ? (
              <>
                <span
                  className="px-3 py-1 rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: getRarityColor(rarity) }}
                >
                  {rarity}
                </span>
                <span className="text-sm text-pocket-text-light">
                  Base Total: {statTotal}
                </span>
              </>
            ) : (
              <>
                <span
                  className="px-3 py-1 rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: getGradeColor(grade) }}
                >
                  {grade}
                </span>
                <span className="text-sm text-pocket-text-light">
                  Total: {statTotal}
                </span>
              </>
            )}
          </div>
          {isBasePokemon && limitBreakLevel > 0 && (
            <p className="text-sm text-pocket-green font-semibold mt-1">
              +{limitBreakLevel * 5}% Base Stats from Limit Break
            </p>
          )}
          {showTrainedMetadata && pokemonData.gymsDefeated !== undefined && pokemonData.completedAt && (
            <div className="flex items-center justify-center gap-2 mt-2 text-xs text-pocket-text-light">
              <div className="flex items-center gap-1">
                <Shield size={12} className="text-amber-500" />
                <span>{pokemonData.gymsDefeated || 0}/8 Gyms</span>
              </div>
              <span>•</span>
              <span>{new Date(pokemonData.completedAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="bg-pocket-bg rounded-xl p-3 mb-3">
          <h4 className="font-bold text-pocket-text text-sm mb-2">
            {isBasePokemon ? `Base Stats${limitBreakLevel > 0 ? ' (with Limit Break)' : ''}` : 'Stats'}
          </h4>
          {isBasePokemon ? (
            <div className="space-y-1.5 text-xs">
              {Object.entries(effectiveStats).map(([stat, value]) => (
                <div key={stat} className="flex items-center gap-2">
                  <span className="text-pocket-text-light w-16">{stat}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-pocket-blue"
                      style={{ width: `${Math.min(100, (value / 150) * 100)}%` }}
                    />
                  </div>
                  <span className="font-bold text-pocket-text w-10 text-right">{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <StatRadarChart stats={effectiveStats} size={160} color="#3B82F6" />
          )}
        </div>

        {/* Stat Preferences (only for base Pokemon) */}
        {isBasePokemon && pokemonData.statPreferences && (
          <div className="bg-amber-50 rounded-xl p-3 mb-3">
            <h4 className="font-bold text-amber-700 text-sm mb-2">Training Preferences</h4>
            <div className="text-xs space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-pocket-text">Preferred Stat:</span>
                <span className="font-bold text-amber-600">{pokemonData.statPreferences.preferred} (+20%)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-pocket-text">Secondary Stat:</span>
                <span className="font-bold text-amber-500">{pokemonData.statPreferences.secondary} (+10%)</span>
              </div>
            </div>
          </div>
        )}

        {/* Strategy Aptitudes */}
        {pokemonData.strategyAptitudes && Object.keys(pokemonData.strategyAptitudes).length > 0 && (
          <div className="bg-purple-50 rounded-xl p-3 mb-3">
            <h4 className="font-bold text-purple-700 text-sm mb-2">Strategy Aptitudes</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {Object.entries(pokemonData.strategyAptitudes).map(([strategy, aptitudeGrade]) => (
                <div
                  key={strategy}
                  className="flex justify-between items-center px-2 py-1 rounded bg-white"
                >
                  <span className="text-pocket-text">{strategy}</span>
                  <span
                    className="px-2 py-0.5 rounded text-white font-bold"
                    style={{ backgroundColor: getAptitudeColor(aptitudeGrade) }}
                  >
                    {aptitudeGrade}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Type Aptitudes */}
        {pokemonData.typeAptitudes && Object.keys(pokemonData.typeAptitudes).length > 0 && (
          <div className="bg-blue-50 rounded-xl p-3 mb-3">
            <h4 className="font-bold text-blue-700 text-sm mb-2">Type Aptitudes</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {Object.entries(pokemonData.typeAptitudes).map(([color, aptitudeGrade]) => {
                const typeName = colorToType[color] || color;
                return (
                  <div
                    key={color}
                    className="flex justify-between items-center px-2 py-1 rounded bg-white"
                  >
                    <TypeIcon type={typeName} size={16} showLabel={true} />
                    <span
                      className="px-2 py-0.5 rounded text-white font-bold"
                      style={{ backgroundColor: getAptitudeColor(aptitudeGrade) }}
                    >
                      {aptitudeGrade}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Learned Moves */}
        {(pokemonData.defaultAbilities || pokemonData.abilities) &&
         (pokemonData.defaultAbilities?.length > 0 || pokemonData.abilities?.length > 0) && (
          <div className="bg-green-50 rounded-xl p-3 mb-3">
            <h4 className="font-bold text-green-700 text-sm mb-2">
              {isBasePokemon
                ? `Default Moves (${pokemonData.defaultAbilities.length})`
                : `Known Moves (${pokemonData.abilities.length})`}
            </h4>
            <div className="space-y-1.5 text-xs max-h-48 overflow-y-auto">
              {(pokemonData.defaultAbilities || pokemonData.abilities).map((moveName, idx) => {
                const move = MOVES && MOVES[moveName];
                const moveType = move?.type || primaryType || 'Normal';
                const typeColor = TYPE_COLORS[moveType] || '#A8A878';
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-2 py-1.5 rounded bg-white border-l-4"
                    style={{ borderLeftColor: typeColor }}
                  >
                    <div className="flex items-center gap-2">
                      <TypeBadge type={moveType} size={12} />
                      <span className="font-semibold text-pocket-text">{moveName}</span>
                    </div>
                    {move && (
                      <div className="flex items-center gap-2 text-pocket-text-light text-xs">
                        <span className="flex items-center gap-0.5" title="Damage"><Swords size={12} className="text-orange-500" />{move.damage}</span>
                        <span className="flex items-center gap-0.5" title="Stamina"><Zap size={12} className="text-yellow-500" />{move.stamina}</span>
                        <span className="flex items-center gap-0.5" title="Cooldown"><Clock size={12} className="text-pink-600" />{move.cooldown}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Learnable Moves (only for base Pokemon) */}
        {isBasePokemon && pokemonData.learnableAbilities && pokemonData.learnableAbilities.length > 0 && (
          <div className="bg-blue-50 rounded-xl p-3 mb-3">
            <h4 className="font-bold text-blue-700 text-sm mb-2">
              Learnable Moves ({pokemonData.learnableAbilities.length})
            </h4>
            <div className="space-y-1.5 text-xs max-h-48 overflow-y-auto">
              {pokemonData.learnableAbilities.map((moveName, idx) => {
                const move = MOVES && MOVES[moveName];
                const moveType = move?.type || primaryType || 'Normal';
                const typeColor = TYPE_COLORS[moveType] || '#A8A878';
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-2 py-1.5 rounded bg-white border-l-4"
                    style={{ borderLeftColor: typeColor }}
                  >
                    <div className="flex items-center gap-2">
                      <TypeBadge type={moveType} size={12} />
                      <span className="font-semibold text-pocket-text">{moveName}</span>
                    </div>
                    {move && (
                      <div className="flex items-center gap-2 text-pocket-text-light text-xs">
                        <span className="flex items-center gap-0.5" title="Damage"><Swords size={12} className="text-orange-500" />{move.damage}</span>
                        <span className="flex items-center gap-0.5" title="Stamina"><Zap size={12} className="text-yellow-500" />{move.stamina}</span>
                        <span className="flex items-center gap-0.5" title="Cooldown"><Clock size={12} className="text-pink-600" />{move.cooldown}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Inspirations (only for trained Pokemon) */}
        {!isBasePokemon && pokemonData.inspirations &&
         pokemonData.inspirations.stat && pokemonData.inspirations.aptitude && (
          <div className="bg-amber-50 rounded-xl p-3 mb-3">
            <h4 className="font-bold text-amber-700 text-sm mb-2">Inspirations</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-pocket-text">{pokemonData.inspirations.stat.name}</span>
                <div className="flex gap-0.5">
                  {[...Array(pokemonData.inspirations.stat.stars || 0)].map((_, i) => (
                    <Star key={i} size={10} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-pocket-text">
                  {colorToType[pokemonData.inspirations.aptitude.name] || pokemonData.inspirations.aptitude.name}
                </span>
                <div className="flex gap-0.5">
                  {[...Array(pokemonData.inspirations.aptitude.stars || 0)].map((_, i) => (
                    <Star key={i} size={10} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
              {pokemonData.inspirations.strategy && (
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-pocket-text">{pokemonData.inspirations.strategy.name}</span>
                  <div className="flex gap-0.5">
                    {[...Array(pokemonData.inspirations.strategy.stars || 0)].map((_, i) => (
                      <Star key={i} size={10} className="text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {renderActions ? renderActions({ pokemonData, displayName, isBasePokemon }) : (
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl bg-pocket-bg text-pocket-text-light font-semibold hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PokemonDetailModal;
